import React from "react";
import {
  Box,
  CardActionArea,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { formatearMonto } from "../../helpers/formato";

// Creado una sola vez a nivel de módulo (construir un Intl.NumberFormat es caro).
const FORMATO_ENTERO = new Intl.NumberFormat("es-NI", { maximumFractionDigits: 0 });

const formatearValor = (valor, formato, moneda) => {
  if (valor === null || valor === undefined || valor === "") return "—";
  if (formato === "monto") return formatearMonto(valor, moneda);
  if (formato === "entero") {
    const numero = Number(valor);
    return Number.isFinite(numero) ? FORMATO_ENTERO.format(numero) : "—";
  }
  return String(valor);
};

/**
 * Tarjeta de indicador (KPI). La cifra es lo más grande; el título es secundario.
 * El color del tono solo tiñe el ícono: la cifra mantiene el contraste del texto principal.
 *
 * @param {object} props
 * @param {React.ReactNode} props.titulo
 * @param {number|string} props.valor Ya formateado, o un número junto con `formato`.
 * @param {"monto"|"entero"} [props.formato] Si se indica, `valor` se formatea (monto con `moneda`).
 * @param {string} [props.moneda] "C$" (por defecto) o "$"; solo con `formato="monto"`.
 * @param {React.ReactNode} [props.icono] Elemento de ícono (decorativo).
 * @param {"primary"|"success"|"warning"|"error"|"info"} [props.tono] Por defecto "primary".
 * @param {React.ReactNode} [props.detalle] Línea de contexto bajo la cifra.
 * @param {boolean} [props.cargando] Muestra un esqueleto en vez de la cifra (nunca "0" ni "C$0.00").
 * @param {Function} [props.onClick] Si se pasa, toda la tarjeta es un botón.
 */
export const KpiCard = ({
  titulo,
  valor,
  formato,
  moneda = "C$",
  icono,
  tono = "primary",
  detalle,
  cargando = false,
  onClick,
}) => {
  // Con onClick el contenido va dentro de un <button>: solo elementos en línea (span),
  // nunca div ni p. Cada span lleva display de bloque o flex para conservar el aspecto.
  const contenido = (
    <Stack
      component="span"
      direction="row"
      spacing={2}
      alignItems="flex-start"
      sx={{ p: 2.5, textAlign: "left", width: "100%" }}
    >
      <Stack component="span" spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          component="span"
          variant="body2"
          sx={{ display: "block", color: "text.secondary", fontWeight: 700 }}
        >
          {titulo}
        </Typography>

        {cargando ? (
          <Skeleton
            variant="text"
            width="60%"
            sx={{
              fontSize: "2rem",
              "@media (prefers-reduced-motion: reduce)": { animation: "none" },
            }}
          />
        ) : (
          <Typography
            variant="monto"
            component="span"
            sx={{
              display: "block",
              fontSize: { xs: "1.5rem", sm: "1.875rem" },
              fontWeight: 700,
              lineHeight: 1.2,
              color: "text.primary",
              overflowWrap: "anywhere",
            }}
          >
            {formatearValor(valor, formato, moneda)}
          </Typography>
        )}

        {/* Mientras carga, un esqueleto del alto del detalle: la tarjeta no crece al llegar el dato. */}
        {detalle && cargando && (
          <Skeleton
            variant="text"
            width="80%"
            sx={{
              fontSize: "0.75rem",
              "@media (prefers-reduced-motion: reduce)": { animation: "none" },
            }}
          />
        )}
        {detalle && !cargando && (
          <Typography
            component="span"
            variant="caption"
            sx={{ display: "block", color: "text.secondary" }}
          >
            {detalle}
          </Typography>
        )}
      </Stack>

      {icono && (
        <Box
          component="span"
          aria-hidden="true"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            width: 44,
            height: 44,
            borderRadius: 1,
            bgcolor: (theme) =>
              theme.palette.soft?.[tono] ??
              theme.palette.soft?.primary ??
              theme.palette.action.hover,
            color: (theme) => theme.palette[tono]?.main ?? theme.palette.primary.main,
          }}
        >
          {icono}
        </Box>
      )}
    </Stack>
  );

  return (
    // Alto completo: en una fila de indicadores todas las tarjetas miden lo mismo, aunque una lleve `detalle`.
    <Paper sx={{ overflow: "hidden", height: "100%" }} aria-busy={cargando || undefined}>
      {onClick ? (
        <CardActionArea
          onClick={onClick}
          disabled={cargando}
          sx={{
            borderRadius: "inherit",
            height: "100%",
            display: "flex",
            alignItems: "flex-start",
            "&:focus-visible": {
              outline: (theme) => `2px solid ${theme.palette.primary.main}`,
              outlineOffset: "-2px",
            },
          }}
        >
          {contenido}
        </CardActionArea>
      ) : (
        contenido
      )}
    </Paper>
  );
};
